import { useEffect, useMemo, useState } from 'react'
import { isAxiosError } from 'axios'
import { Check, RotateCcw, Save, Shield, TriangleAlert } from 'lucide-react'

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { SkeletonRows } from '@/components/ui/skeleton'

import {
  useReplaceRolePermissions,
  useRoleMatrix,
  type Grants,
  type PermissionScope,
  type RoleRow,
} from './rolesApi'

/**
 * The role / permission matrix editor.
 *
 * Exists to tune permissions before launch, and is expected to be switched off
 * once the matrix settles — a customer reshaping their own roles produces
 * support load and self-inflicted lockouts. It is therefore gated on a tenant
 * flag (`admin.role_editor.enabled`) as well as on ROLE_MANAGE, and renders an
 * explanation rather than an error when the flag is off.
 *
 * Editing is local until Save: the draft is diffed against what the server
 * returned, so the button can say exactly what is about to change. A blind
 * "Save" on a 61-row permission matrix is how people discover afterwards that
 * they revoked something they did not mean to.
 */
export function RolesAdminPage() {
  const { data, isLoading, error } = useRoleMatrix()
  const replace = useReplaceRolePermissions()

  const [selectedRoleId, setSelectedRoleId] = useState<string | null>(null)
  const [draft, setDraft] = useState<Grants>({})

  const roles = data?.matrix?.roles ?? []
  const selected: RoleRow | undefined = useMemo(
    () => roles.find((r) => r.roleId === selectedRoleId) ?? roles[0],
    [roles, selectedRoleId],
  )

  // Reset the draft whenever the selected role changes or the server data
  // refreshes, so a stale edit can never be saved against a different role.
  useEffect(() => {
    if (selected) setDraft({ ...selected.grants })
  }, [selected?.roleId, selected?.grants])

  const diff = useMemo(() => {
    if (!selected) return { granted: [], revoked: [], rescoped: [] }
    const granted: string[] = []
    const revoked: string[] = []
    const rescoped: string[] = []
    for (const [code, scope] of Object.entries(draft)) {
      const before = selected.grants[code]
      if (!before) granted.push(code)
      else if (before !== scope) rescoped.push(code)
    }
    for (const code of Object.keys(selected.grants)) {
      if (!draft[code]) revoked.push(code)
    }
    return { granted, revoked, rescoped }
  }, [draft, selected])

  const changeCount = diff.granted.length + diff.revoked.length + diff.rescoped.length

  if (isLoading) return <SkeletonRows rows={8} />

  if (error) {
    return (
      <Alert>
        <AlertTitle>Could not load the permission matrix</AlertTitle>
        <AlertDescription>
          {isAxiosError(error) ? (error.response?.data as string) ?? error.message : String(error)}
        </AlertDescription>
      </Alert>
    )
  }

  if (data && !data.enabled) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-4 w-4" /> Role editor is off for this tenant
          </CardTitle>
          <CardDescription>
            Permissions are managed by Graviton for this tenant. Enable it by setting{' '}
            <code className="rounded bg-slate-100 px-1 py-0.5 text-xs">
              admin.role_editor.enabled
            </code>{' '}
            to <code className="rounded bg-slate-100 px-1 py-0.5 text-xs">true</code> in tenant
            settings — no redeploy needed, and it can be turned back off the same way.
          </CardDescription>
        </CardHeader>
      </Card>
    )
  }

  const groups = data?.matrix?.groups ?? []
  const scopes = data?.matrix?.scopes ?? []

  function toggle(code: string) {
    setDraft((d) => {
      const next = { ...d }
      if (next[code]) delete next[code]
      else next[code] = (selected?.grants[code] ?? 'TENANT') as PermissionScope
      return next
    })
  }

  function setScope(code: string, scope: PermissionScope) {
    setDraft((d) => ({ ...d, [code]: scope }))
  }

  return (
    <div className="space-y-4">
      <Alert>
        <TriangleAlert className="h-4 w-4" />
        <AlertTitle>Pre-launch tuning surface</AlertTitle>
        <AlertDescription>
          Changes take effect on each user&apos;s next request — no re-login needed. A revoked
          permission stays revoked across restarts; the boot re-seed will not put it back.
        </AlertDescription>
      </Alert>

      <div className="grid gap-4 md:grid-cols-[15rem_1fr]">
        <Card className="h-fit">
          <CardHeader>
            <CardTitle>Roles</CardTitle>
            <CardDescription>{roles.length} defined</CardDescription>
          </CardHeader>
          <CardContent className="space-y-1">
            {roles.map((r) => {
              const active = r.roleId === selected?.roleId
              return (
                <button
                  key={r.roleId}
                  onClick={() => setSelectedRoleId(r.roleId)}
                  className={
                    'flex w-full items-center justify-between rounded-md px-2 py-1.5 text-left text-sm ' +
                    (active ? 'bg-navy-100 font-medium text-navy-900' : 'hover:bg-slate-50')
                  }
                >
                  <span className="truncate">{r.displayName}</span>
                  <span className="ml-2 shrink-0 tabular-nums text-xs text-slate-500">
                    {Object.keys(r.grants).length}
                  </span>
                </button>
              )
            })}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex-row items-start justify-between gap-4 space-y-0">
            <div>
              <CardTitle className="flex items-center gap-2">
                {selected?.displayName}
                {selected?.isSystem && <Badge>system</Badge>}
              </CardTitle>
              <CardDescription>{selected?.description}</CardDescription>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <Button
                variant="ghost"
                disabled={changeCount === 0 || replace.isPending}
                onClick={() => selected && setDraft({ ...selected.grants })}
              >
                <RotateCcw className="mr-1.5 h-3.5 w-3.5" /> Reset
              </Button>
              <Button
                disabled={changeCount === 0 || replace.isPending}
                onClick={() =>
                  selected && replace.mutate({ roleId: selected.roleId, grants: draft })
                }
              >
                <Save className="mr-1.5 h-3.5 w-3.5" />
                {changeCount === 0 ? 'No changes' : `Save ${changeCount} change${changeCount === 1 ? '' : 's'}`}
              </Button>
            </div>
          </CardHeader>

          <CardContent className="space-y-4">
            {changeCount > 0 && (
              <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">
                {diff.granted.length > 0 && <div>+ granting {diff.granted.join(', ')}</div>}
                {diff.revoked.length > 0 && <div>− revoking {diff.revoked.join(', ')}</div>}
                {diff.rescoped.length > 0 && (
                  <div>
                    ~ rescoping{' '}
                    {diff.rescoped.map((c) => `${c} → ${draft[c]}`).join(', ')}
                  </div>
                )}
              </div>
            )}

            {replace.isError && (
              <Alert>
                <AlertTitle>Could not save</AlertTitle>
                <AlertDescription>
                  {isAxiosError(replace.error)
                    ? ((replace.error.response?.data as { message?: string })?.message ??
                      replace.error.message)
                    : String(replace.error)}
                </AlertDescription>
              </Alert>
            )}

            {replace.isSuccess && changeCount === 0 && (
              <div className="flex items-center gap-1.5 text-xs text-success">
                <Check className="h-3.5 w-3.5" /> Saved.
              </div>
            )}

            {groups.map((g) => (
              <div key={g.name}>
                <h3 className="mb-1.5 text-2xs font-semibold uppercase tracking-wider text-slate-500">
                  {g.name}
                </h3>
                <div className="divide-y divide-slate-100 rounded-md border border-slate-200">
                  {g.permissions.map((code) => {
                    const held = Boolean(draft[code])
                    return (
                      <div key={code} className="flex items-center gap-3 px-3 py-1.5">
                        <input
                          id={code}
                          type="checkbox"
                          checked={held}
                          onChange={() => toggle(code)}
                          className="h-3.5 w-3.5 rounded border-slate-300"
                        />
                        <label htmlFor={code} className="flex-1 font-mono text-xs text-navy-900">
                          {code}
                        </label>
                        <select
                          value={draft[code] ?? ''}
                          disabled={!held}
                          onChange={(e) => setScope(code, e.target.value as PermissionScope)}
                          className="rounded border border-slate-200 bg-white px-1.5 py-0.5 text-xs disabled:opacity-30"
                        >
                          {!held && <option value="">—</option>}
                          {scopes.map((s) => (
                            <option key={s} value={s}>
                              {s}
                            </option>
                          ))}
                        </select>
                      </div>
                    )
                  })}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
